import { useTracker, objectTypes, actionNames } from "@newsela/snitch";
import {
  getSuccessToastProps,
  getErrorToastProps,
} from "@/assignments/utils/assignmentToastHelpers";
import { getHasUnconsentedSRC } from "@/assignments/utils/assignmentUtils";
import {
  getIsAssignmentValid,
  getInvalidAssignmentMessage,
} from "@/assignments/utils/assignmentValidationHelpers";
import globalToasterRef from "@/base/globalToasterRef";

import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import useFocusManager from "@/view/utils/useFocusManager";

/**
 * shouldDisplayAdvisoryModal returns a boolean indicating whether the consent
 * modal needs to be displayed. The modal is displayed when the assignment includes
 * uncosented student-restricted content and the teacher hadn't already consetnted to it.
 */
const shouldDisplayAdvisoryModal = (
  assignment,
  isAssigningStudentRestrictedContent,
) => {
  const isNewAssignment = !assignment?.content_id;
  const isDraft = assignment?.is_draft;
  const hasUnconsentedMembers = getHasUnconsentedSRC(
    assignment?.assignment_members,
  );

  return (
    isAssigningStudentRestrictedContent &&
    (isNewAssignment || hasUnconsentedMembers || isDraft)
  );
};

const usePublishAssignment = ({
  currentContentLevelId,
  handleProvideConsent,
  handleSave,
  isDisabled,
  setAdvisoryModalActive,
  updatedAssignment,
  maxScheduleDate,
  verifyAssigneeSelection,
}) => {
  const { trackAction } = useTracker();
  const { focusElement } = useFocusManager();

  const assignmentId = updatedAssignment?.content_id;
  const isPublishedAssignment = !!assignmentId && !updatedAssignment?.is_draft;

  const getEventProperties = (actionName, description) => ({
    object_type: objectTypes.assignment,
    action_name: actionName,
    dimension_properties: {
      content_id: currentContentLevelId,
      content_ids: updatedAssignment?.assignment_members?.map(
        (member) => member.content_id,
      ),
      content_type: "Article",
      description,
      object_id: assignmentId,
    },
  });

  const saveAssignmentEventProperties = isPublishedAssignment
    ? getEventProperties(actionNames.save, "Save assignment")
    : getEventProperties(actionNames.create, "Create assignment");

  const provideConsentEventProperties = getEventProperties(
    actionNames.update,
    "Provide consent",
  );

  // Toasts
  const successMessage = `You have successfully ${isPublishedAssignment ? "saved" : "created"} an assignment.`;
  const saveSuccessToastProps = getSuccessToastProps(
    successMessage,
    currentContentLevelId,
    assignmentId,
  );

  const saveErrorMessage = `Assignment ${isPublishedAssignment ? "save" : "creation"} failed, please try again.`;
  const saveErrorToastProps = getErrorToastProps(saveErrorMessage);

  const invalidAssignmentErrorMessage = getInvalidAssignmentMessage(
    updatedAssignment,
    isPublishedAssignment,
    maxScheduleDate,
  );
  const invalidAssignmentToastProps = getErrorToastProps(
    invalidAssignmentErrorMessage,
  );
  const provideConsentAndPublish = () => {
    // TODO: CS-UI-HACK: This is a hack and it makes me sad we have to do it. It's required to
    // ensure that Content Settings assignment publishing works correctly. Currently publish logic is
    // spread out between usePublishAssignment and ShareAssignmentModalButtons. Move it into one hook,
    // and use clean reducer state.
    const headerId = handleProvideConsent();
    setAdvisoryModalActive(false);
    const assignment = {
      ...updatedAssignment,
      // There is no good way to sequence assignment POST only after the dispatch took action and updated the reducer,
      // so brute force it by manually setting content_settings_override here. This won't cause data consistency issues
      // because we'll re-fetch the assignment from API after a successful post and re-initialize the assignment state.
      content_settings_override: headerId,
    };
    if (headerId) {
      handleSave({
        eventProperties: saveAssignmentEventProperties,
        saveSuccessToastProps,
        saveErrorToastProps,
        isDraft: false,
        assignment,
      });
      trackAction(provideConsentEventProperties);
    }
  };

  const handlePublish = () => {
    const isValidAssignment = getIsAssignmentValid(
      updatedAssignment,
      false,
      maxScheduleDate,
    );
    if (isDisabled) return;
    if (!isValidAssignment) {
      verifyAssigneeSelection(updatedAssignment);
      return globalToasterRef.current.displayToast(invalidAssignmentToastProps);
    }

    handleSave({
      eventProperties: saveAssignmentEventProperties,
      saveSuccessToastProps,
      saveErrorToastProps,
      isDraft: false,
    });

    focusElement(focusElementRefKeys.assignmentEditorHeading);
  };

  return { handlePublish, isPublishedAssignment, provideConsentAndPublish };
};

export { shouldDisplayAdvisoryModal, usePublishAssignment };
